import { AdminGroupSelectItem } from "models/AdminGroupSelectItem";
import { AdminUserData } from "models/AdminUserData";
import { AdminUserSelectItem } from "models/AdminUserSelectItem";
import { PaginatedDesignSearchRequestDto } from "models/PaginatedDesignSearchRequestDto";
import { PaginatedResponseDto } from "models/PaginatedResponseDto";
import { PaginatedSearchRequestDto } from "models/PaginatedSearchRequestDto";
import { PaginatedUserSearchRequestDto } from "models/PaginatedUserSearchRequestDto";
import { Printer } from "models/Printer";
import { ExhibitionAdminSession } from "models/ExhibitionSession";

export async function fetchAdminUsers(
  api: any, paginatedRequestDto: PaginatedSearchRequestDto
): Promise<PaginatedResponseDto<AdminUserData> | undefined> {
  const res = await api.get(`/api/admin/users?${paginatedRequestDto.toQueryString()}`);
  if (res.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    return;
  }
  return res?.body;
}

export async function fetchUserSelectItems(api: any, paginatedRequestDto: PaginatedUserSearchRequestDto) {
  const res = await api.get(`/api/admin/users/all?${paginatedRequestDto.toQueryString()}`);
  if (res.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    return;
  }
  const mappedData: AdminUserSelectItem[] = Array.isArray(res?.body?.data) && res.body.data.length > 0
    ? res.body.data.map((u: any) => new AdminUserSelectItem(u.id, { name: u.name, username: u.username }))
    : [];
  return mappedData;
}

export async function fetchAdminDesigns(
  api: any, paginatedRequestDto: PaginatedDesignSearchRequestDto
): Promise<PaginatedResponseDto<AdminApiDesignData> | undefined> {
  const res = await api.get(`/api/admin/designs?${paginatedRequestDto.toQueryString()}`);
  return res?.body;
}

export async function fetchHubDesigns(
  api: any, paginatedRequestDto: PaginatedDesignSearchRequestDto
): Promise<PaginatedResponseDto<AdminApiDesignData> | undefined> {
  const res = await api.get(`/api/send_to_hub/designs?${paginatedRequestDto.toQueryString()}`);
  return res?.body;
}


export async function fetchAdminVoiceUsage(
  api: any, paginatedRequestDto: PaginatedSearchRequestDto
): Promise<PaginatedResponseDto<any> | undefined> {
  const res = await api.get(`/api/admin/voice-usage?${paginatedRequestDto.toQueryString()}`);
  if (res.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    return;
  }
  if (!res.ok) {
    throw new Error(res.body?.message || res.body?.Message || 'Failed to fetch voice usage');
  }
  return res?.body;
}

export async function fetchAdminVoiceSessions(
  api: any, userId: number, paginatedRequestDto: PaginatedSearchRequestDto
): Promise<PaginatedResponseDto<any> | undefined> {
  const res = await api.get(`/api/admin/voice-usage/${userId}/sessions?${paginatedRequestDto.toQueryString()}`);
  if (res.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    return;
  }
  if (!res.ok) {
    throw new Error(res.body?.message || res.body?.Message || 'Failed to fetch voice sessions');
  }
  return res?.body;
}

export async function resetAdminVoiceUsage(api: any, userId: number): Promise<boolean> {
  const res = await api.delete(`/api/admin/voice-usage/${userId}`);
  if (res.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    return false;
  }
  if (!res.ok) {
    throw new Error(res.body?.message || res.body?.Message || 'Failed to reset voice usage');
  }
  return true;
}

export async function setAdminVoiceAccess(api: any, userId: number, enabled: boolean): Promise<boolean> {
  const res = await api.patch(`/api/admin/voice-usage/${userId}/access`, { enabled });
  if (res.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    return false;
  }
  if (!res.ok) {
    throw new Error(res.body?.message || res.body?.Message || 'Failed to update voice access');
  }
  return true;
}

export async function fetchAdminTickets(
  api: any, paginatedRequestDto: PaginatedSearchRequestDto
): Promise<PaginatedResponseDto<AdminTicketData> | undefined> {
  const res = await api.get(`/api/feedback?${paginatedRequestDto.toQueryString()}`);
  if (res.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    return;
  }
  return res?.body;
}

export async function fetchAllGroups(api: any): Promise<AdminGroupSelectItem[]> {
  const res = await api.get(`/api/admin/groups`);
  if (res.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    return [];
  }
  const mappedData: AdminGroupSelectItem[] = Array.isArray(res?.body) && res.body.length > 0
  ? res.body.map((u: any) => new AdminGroupSelectItem(u.groupId, { groupName: u.groupName }))
  : [];
  return mappedData;
}

export async function getPrintersByUserIdList(api: any, userIdList: number[]) {
  const res = await api.post('/api/admin/users/printers', userIdList);
  if (res.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    return [];
  }
  const mappedData: Printer[] = Array.isArray(res?.body) && res.body.length > 0
    ? res.body.map((p: any): Printer => ({
      printerId: p.printerId,
      name: p.name,
      ip: p.ip,
      setting: p.setting,
      defaultPrinter: p.defaultPrinter,
      userId: p.userId
    }))
    : [];
    return mappedData;
}

export async function fetchExhibitionSessions(
  api: any,
  paginatedRequestDto: PaginatedSearchRequestDto,
  active: boolean | null = true
): Promise<PaginatedResponseDto<ExhibitionAdminSession> | undefined> {
  const suffix = active === null ? "" : `&active=${active}`;
  const res = await api.get(
    `/api/admin/exhibition/sessions?${paginatedRequestDto.toQueryString()}${suffix}`
  );
  if (!res.ok) throw new Error(res.body?.message || "Failed to load exhibition sessions");
  return res.body;
}

export async function deleteExhibitionSession(api: any, sessionId: number): Promise<void> {
  const res = await api.delete(`/api/admin/exhibition/sessions/${sessionId}`);
  if (!res.ok) throw new Error(res.body?.message || "Failed to end exhibition session");
}
